import { toaster } from "@/components/ui/toaster";
import { getFieldVisits, GetFieldVisitsProps } from "@/services/field-visit.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { FieldVisitListResponse } from "./field-visit.schema";

interface FetchFieldVisitsHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<FieldVisitListResponse | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchFieldVisitsHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchFieldVisitsHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getFieldVisits({
      ...filters,
      CuentaOrigen: filters?.CuentaOrigen?.value ?? filters?.CuentaOrigen,
      FechaDeseada: filters?.FechaDeseada,
      Estados: filters?.Estados,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    } as GetFieldVisitsProps);

    setList(response ?? undefined);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudieron obtener las visitas a campo.",
        ),
      });
    }

    throw error;
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitFiltersHandlerProps {
  event: FieldValues;
  setPage: Dispatch<SetStateAction<number>>;
  fetchList: (filters?: any) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  filtersEmpty: boolean;
}

export const submitFiltersHandler = async ({
  event,
  setPage,
  fetchList,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  filtersEmpty,
}: SubmitFiltersHandlerProps) => {
  const FechaDeseada = event?.FechaDeseada
    ? dayjs(event.FechaDeseada).format("YYYY-MM-DD")
    : undefined;

  const Estados = Array.isArray(event?.Estados)
    ? event.Estados.map(Number).filter((v: any) => !Number.isNaN(v))
    : undefined;

  const nextFilters = {
    FechaDeseada,
    CuentaOrigen: event?.CuentaOrigen || undefined,
    Estados,
    "P.Pagina": 1,
  };

  setPage(1);
  setCurrentFilters(nextFilters);
  setFiltersApplied(!filtersEmpty);
  setOpenFilters(false);

  await fetchList(nextFilters);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  fetchList: (filters?: any) => Promise<void>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  setCurrentFilters,
  fetchList,
}: ClearFiltersHandlerProps) => {
  setValue("FechaDeseada", undefined);
  setValue("CuentaOrigen", undefined);
  setValue("Estados", []);

  setFiltersApplied(false);
  setOpenFilters(false);
  setPage(1);
  setCurrentFilters({});

  await fetchList({ "P.Pagina": 1 });
};
